import {Add, TuneOutlined} from '@mui/icons-material';
import {Box, Button, Dialog, IconButton, Typography} from '@mui/material';
import {FC, ReactNode, useState} from 'react';
import {useIntl} from 'react-intl';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {phoneSheetHandleSx} from 'src/components/mobile/mobileDesign.sx';
import {
    bandLabelSx,
    addButtonSx,
    bandSx,
    clearAllSx,
    countSx,
    dividerSx,
    fieldSx,
    filterSheetSx,
    filterTriggerSx,
    headActionsSx,
    headRowSx,
    iconBadgeSx,
    sheetApplySx,
    sheetBodySx,
    sheetClearSx,
    sheetFieldSx,
    sheetFooterSx,
    sheetGroupLabelSx,
    sheetGroupSx,
    sheetHeadSx,
    sheetTitleSx,
    spacerSx,
    titleGroupSx,
    titleSx,
} from './FilterBar.sx';

export type FilterBarField = {
    id: string;
    /** The control itself — a select, an autocomplete, whatever the filter needs. */
    control: ReactNode;
    /** Wider than the default, for a field whose values are long — a location or a tariff name. */
    wide?: boolean;
};

type FilterBarProps = {
    /** The name of the list. Lives in the card, so there is no separate page heading above it. */
    title?: string;
    /** The subject badge beside the title — what tells the eye which list this is before it reads. */
    icon?: ReactNode;
    /** Sits beside the title. The row count, when the table knows it. */
    subtitle?: string;
    /** Search, export, "add" — anything about the page rather than about what it is showing. */
    actions?: ReactNode;
    /** Tabs, where the list has them. Above the band, so switching tab keeps the filters in view. */
    tabs?: ReactNode;
    fields?: FilterBarField[];
    /** The period select, on the lists that are filtered by one. */
    period?: ReactNode;
    /** Qualifies the period. The start/stop basis on the session screens. */
    periodQualifier?: ReactNode;
    onClearAll?: () => void;
    /** Drives only whether Clear all is enabled — it is on screen either way. */
    hasActiveFilters?: boolean;
    /** The list's own "add" action, when it has one. Sits with the search rather than above the card. */
    onAdd?: () => void;
    /** A translation id, not a phrase — every caller passes the key its page header used to pass. */
    addLabel?: string;
};

/**
 * Two tiers: what you are looking at, then what you do with it.
 *
 * Every list screen carried its own copy of a toolbar - a search field and a Filter icon opening a
 * popover with a form behind it. The state of that form was invisible: the only sign a filter was on
 * was the icon turning blue, so a list showing eleven of four hundred rows looked exactly like a list
 * showing all four hundred, and finding out which filter was set cost a click.
 *
 * Here the filters are on screen and carry their own values, the heading says how many rows matched,
 * and the whole thing is bounded by the card it belongs to. The two rows size independently, so the
 * band can wrap without moving the heading above it.
 *
 * Below md the band is a sheet instead. The same controls, the same state, the same handlers — but
 * a band that wraps to one control per line is four lines of grey above a list that has none of the
 * screen left, and on a phone the list is the thing that has to be visible. The trigger carries the
 * one fact the band made visible: that something is set.
 */
export const FilterBar: FC<FilterBarProps> = ({
                                                  title,
                                                  icon,
                                                  subtitle,
                                                  actions,
                                                  tabs,
                                                  fields = [],
                                                  period,
                                                  periodQualifier,
                                                  onClearAll,
                                                  hasActiveFilters,
                                                  onAdd,
                                                  addLabel,
                                              }) => {
    const intl = useIntl();
    const {isCompact} = useScreenTier();
    const [sheetOpen, setSheetOpen] = useState(false);
    const showBand = fields.length > 0 || Boolean(period);
    const showSheet = isCompact && showBand;

    const closeSheet = () => {
        setSheetOpen(false);
    };

    return (
        <>
            <Box sx={headRowSx}>
                <Box sx={titleGroupSx}>
                    {icon && <Box sx={iconBadgeSx}>{icon}</Box>}
                    {title && (
                        <Typography
                            variant="subtitle1"
                            sx={titleSx}
                        >
                            {title}
                        </Typography>
                    )}
                    {subtitle && (
                        <Typography
                            variant="caption"
                            sx={countSx}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {(actions || onAdd || showSheet) && (
                    <Box sx={headActionsSx}>
                        {actions}
                        {showSheet && (
                            <IconButton
                                sx={filterTriggerSx(hasActiveFilters)}
                                onClick={() => setSheetOpen(true)}
                            >
                                <TuneOutlined/>
                            </IconButton>
                        )}
                        {onAdd && (
                            <Button
                                variant="contained"
                                sx={addButtonSx}
                                startIcon={<Add/>}
                                onClick={onAdd}
                            >
                                {addLabel && intl.formatMessage({id: addLabel})}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            {tabs}

            {showBand && !isCompact && (
                <Box sx={bandSx}>
                    {/* Right-aligned by the band itself, period or not, so the filters end where
                        Clear all does rather than starting under the title. */}
                    {/* Names the band, not the group after it, so it is there even on a table whose
                        only filter is the period: without it that row was a date box floating on a
                        grey strip, with nothing saying it narrowed anything. */}
                    <Typography
                        component="span"
                        sx={bandLabelSx}
                    >
                        {intl.formatMessage({id: 'filters'})}
                    </Typography>

                    {fields.map((field) => (
                        <Box
                            key={field.id}
                            sx={fieldSx(field.wide)}
                        >
                            {field.control}
                        </Box>
                    ))}

                    {/* Only between the two groups, so only where there are two. With no period the
                        filters are the whole band, and a spacer after them pushed Clear all to the
                        far edge with a lake of grey in between. */}
                    {period && fields.length > 0 && <Box sx={spacerSx}/>}

                    {period && (
                        <>
                            {fields.length > 0 && (
                                <>
                                    <Box sx={dividerSx}/>
                                    <Box sx={spacerSx}/>
                                </>
                            )}
                            <Typography
                                component="span"
                                sx={bandLabelSx}
                            >
                                {intl.formatMessage({id: 'period'})}
                            </Typography>
                            {period}
                            {periodQualifier}
                        </>
                    )}

                    {onClearAll && (
                        // Always on screen, disabled when there is nothing to clear. Appearing only
                        // once a filter is set makes the band change width as you use it, and hides
                        // the one control that tells a reader the list can be reset at all.
                        <Button
                            variant="text"
                            color="inherit"
                            sx={clearAllSx}
                            disabled={!hasActiveFilters}
                            onClick={onClearAll}
                        >
                            {intl.formatMessage({id: 'clearAll'})}
                        </Button>
                    )}
                </Box>
            )}

            {showSheet && (
                <Dialog
                    open={sheetOpen}
                    onClose={closeSheet}
                    fullWidth
                    maxWidth={false}
                    sx={filterSheetSx}
                >
                    <Box sx={phoneSheetHandleSx}/>
                    <Box sx={sheetHeadSx}>
                        <Typography sx={sheetTitleSx}>
                            {intl.formatMessage({id: 'filters'})}
                        </Typography>
                        {onClearAll && (
                            <Button
                                variant="text"
                                color="inherit"
                                sx={sheetClearSx}
                                disabled={!hasActiveFilters}
                                onClick={onClearAll}
                            >
                                {intl.formatMessage({id: 'clearAll'})}
                            </Button>
                        )}
                    </Box>

                    <Box sx={sheetBodySx}>
                        {/* The very controls the band holds. They apply as they change, exactly as
                            they do on a desktop — Apply closes the sheet rather than submitting a
                            form, because there is no form here to submit. */}
                        {fields.map((field) => (
                            <Box
                                key={field.id}
                                sx={sheetFieldSx}
                            >
                                {field.control}
                            </Box>
                        ))}

                        {period && (
                            <Box sx={sheetGroupSx}>
                                <Typography
                                    component="span"
                                    sx={sheetGroupLabelSx}
                                >
                                    {intl.formatMessage({id: 'period'})}
                                </Typography>
                                <Box sx={sheetFieldSx}>{period}</Box>
                                {periodQualifier && <Box sx={sheetFieldSx}>{periodQualifier}</Box>}
                            </Box>
                        )}
                    </Box>

                    <Box sx={sheetFooterSx}>
                        <Button
                            variant="contained"
                            sx={sheetApplySx}
                            onClick={closeSheet}
                        >
                            {intl.formatMessage({id: 'apply'})}
                        </Button>
                    </Box>
                </Dialog>
            )}
        </>
    );
};
